import sx from '../../sx.js';
import { themeGet } from '../../utils/theme-get.js';
import React__default from 'react';
import styled from 'styled-components';
import Box from '../Box/Box.js';
import Text from '../Text/Text.js';
import Link from '../Link/Link.js';
import { Card } from '../Card/Card.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const StyledCard = styled(Card).withConfig({
  displayName: "FooterCard__StyledCard",
  componentId: "sc-6dxpem-0"
})(["flex:1 1 308px;@media (min-width:", "){flex:0 1 308px;min-height:308px;}", ""], themeGet('breakpoints.0'), sx);

/**
 * The Getting Started page Footer Card provides additional information to users about products and features, and provides links that give users actions to take.
 *
 * Those actions can be internal, external, or addon actions.
 *
 * The action passed to a Footer Card button's onClick handler is of type FooterCardAction. Every
 * FooterCardAction should have a type, a destination url, label, and tracking object,
 * which itself consists of a destination and a type.
 *
 * Available ActionTypes: "EXTERNAL" | "INTERNAL" | "ADDON";
 *
 * Available DataTrackingTypes: "button" | "link" | "video";
 * 
 * For example, you might provide an array of actions that look like this...
 * 
 * ```javascript
 * const documentationTroubleshooting = {
    type: 'EXTERNAL',
    url: 'https://innovalog.atlassian.net/wiki/x/ZICzBQ',
    label: 'Troubleshooting documentation',
    tracking: {
      dest: 'documentation:troubleshooting',
      type: 'link'
    }
  }
 * ```

...and those actions would be passed into the FooterCard and rendered in the links section:

```jsx

 <Card.Footer>
    <Box
      sx={{
        minHeight: '72px',
        marginTop: '48px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        gap: '8px'
      }}
    >
      {actions?.map(action => (
        <Link>{action.label}</Link>
      ))}
    </Box>
  </Card.Footer>
```
 */
const FooterCard = ({
  title,
  caption,
  actions,
  actionHandler,
  linkData
}) => {
  return /*#__PURE__*/React__default.createElement(StyledCard, {
    sx: {
      borderRadius: '8px',
      border: '1px solid',
      backgroundColor: 'elevation.surface.default.[default]',
      borderColor: 'border.default',
      width: 'clamp(284px, 35%, 300px)',
      padding: 5,
      display: 'flex',
      flexDirection: 'column'
    },
    as: "article"
  }, /*#__PURE__*/React__default.createElement(Card.Content, null, /*#__PURE__*/React__default.createElement(Card.Header, null, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '100%'
    }
  }, /*#__PURE__*/React__default.createElement(Text, {
    as: "h3",
    sx: {
      fontSize: 7,
      minHeight: '64px',
      fontWeight: 'semibold',
      color: 'text.default'
    }
  }, title), /*#__PURE__*/React__default.createElement(Text, {
    as: "p",
    sx: {
      color: 'text.subtlest',
      lineHeight: 'default',
      fontSize: 3
    }
  }, caption))), /*#__PURE__*/React__default.createElement(Card.Body, null), /*#__PURE__*/React__default.createElement(Card.Footer, null, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      // minHeight: '72px',
      marginTop: '48px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      gap: '8px'
    }
  }, linkData ? linkData.map(({
    label,
    sx: currentSx,
    ...linkProps
  }) => {
    return /*#__PURE__*/React__default.createElement(Link, _extends({}, linkProps, {
      sx: {
        fontSize: '3',
        ...currentSx
      }
    }), label);
  }) : actions === null || actions === void 0 ? void 0 : actions.map(action => /*#__PURE__*/React__default.createElement(Link, {
    target: "",
    key: action.label,
    href: action.url,
    sx: {
      fontSize: '3'
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ,
    onClick: () => actionHandler ? actionHandler(action) : null
  }, action.label))))));
};
FooterCard.displayName = "FooterCard";
var FooterCard$1 = FooterCard;

export { FooterCard$1 as default };
